import { useParams, Link } from 'react-router';
import { MapPin, Phone, Clock, Info, ArrowLeft, Navigation } from 'lucide-react';
import { useLocationById, useLocationBySlug } from '@/hooks/useLocations';
import { Button } from '@/components/ui/button';

export const LocationDetailPage = () => {
    const { id } = useParams<{ id: string }>();  // Can be slug or ID
    const isGuid = id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

    const {
        data: locationBySlug,
        isLoading: isLoadingBySlug,
        isError: isSlugError,
        refetch: refetchBySlug,
    } = useLocationBySlug(isGuid ? undefined : id);

    const {
        data: locationById,
        isLoading: isLoadingById,
        isError: isIdError,
        refetch: refetchById,
    } = useLocationById(isGuid ? id : undefined);

    const location = isGuid ? locationById : locationBySlug;
    const isLoading = isGuid ? isLoadingById : isLoadingBySlug;
    const hasError = isGuid ? Boolean(isIdError) : Boolean(isSlugError);
    const addressLines = [
        location?.address?.trim(),
        [location?.city?.trim(), location?.postalCode?.trim(), location?.country?.trim()].filter(Boolean).join(', '),
    ].filter(Boolean);
    const hasAddress = addressLines.length > 0;
    const phone = location?.phone?.trim() || '';
    const email = location?.email?.trim() || '';
    const imageUrl = location?.imageUrl?.trim() || '';
    const pickupInstructions = location?.specialInstructions?.trim() || '';
    const hasContactDetails = Boolean(phone || email);

    if (isLoading) {
        return (
            <div
                className="flex justify-center items-center min-h-[50vh]"
                role="status"
                aria-label="Loading location details"
                aria-live="polite"
            >
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (hasError) {
        const retry = async () => {
            if (isGuid) {
                await refetchById();
                return;
            }

            await refetchBySlug();
        };

        return (
            <div className="flex items-center justify-center min-h-[50vh] bg-white px-6">
                <div className="w-full max-w-xl rounded-3xl border border-destructive/15 bg-destructive/5 p-8 text-center shadow-sm">
                    <h2 className="text-2xl font-semibold text-foreground">We couldn&apos;t load this location</h2>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                        Please try again in a moment. If the problem continues, our team can help you arrange pickup details directly.
                    </p>
                    <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <Button onClick={() => void retry()} className="w-full sm:w-auto">
                            Try again
                        </Button>
                        <Button asChild variant="outline" className="w-full sm:w-auto">
                            <Link to="/contact">Contact our team</Link>
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (!location) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
                <h2 className="text-2xl font-bold text-foreground">Location Not Found</h2>
                <p className="text-muted-foreground">The location you are looking for does not exist.</p>
                <Button asChild variant="outline">
                    <Link to="/locations">Back to Locations</Link>
                </Button>
            </div>
        );
    }

    // Canonical slug — used for the catalogue cross-link below; the page's
    // canonical SEO path is built in the route shell.
    const canonicalSlug = location.slug || location.id;

    // Google Maps Embed URL (using address or coordinates if available)
    // For MVP, we'll construct a search query based on address/city
    const mapQuery = encodeURIComponent(addressLines.join(', '));
    const isTestMode = import.meta.env.MODE === 'test';

    // Note: In production, use a real API key. For now, we might use a placeholder or iframe without key if allowed for dev,
    // or just a link. Since we can't embed without a key easily, I'll use a static map placeholder or just a link.
    // Actually, let's use a simple "Get Directions" link to Google Maps.

    const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

    return (
        <>
            {/* SEO + LocalBusiness JSON-LD lives in the route shell's meta export
                (`app/routes/$locale.locations.$id.tsx`). */}

            <div className="bg-white py-12 sm:py-16">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mb-8">
                        <Link to="/locations" className="flex items-center text-sm font-medium text-primary hover:text-primary">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Locations
                        </Link>
                    </div>

                    <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{location.name}</h1>
                            <p className="mt-4 text-lg leading-8 text-muted-foreground">{location.description}</p>

                            <div className="mt-6">
                                <Button asChild size="lg">
                                    <Link to={`/catalogue/${canonicalSlug}`}>
                                        Browse baby equipment at {location.name}
                                    </Link>
                                </Button>
                            </div>

                            <div className="mt-8 space-y-6">
                                {hasAddress && (
                                    <div className="flex items-start gap-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
                                            <MapPin className="h-6 w-6 text-white" aria-hidden="true" />
                                        </div>
                                        <div>
                                            <h3 className="text-base font-semibold leading-7 text-foreground">Address</h3>
                                            <p className="mt-2 leading-7 text-muted-foreground whitespace-pre-line">
                                                {addressLines.join('\n')}
                                            </p>
                                            <a
                                                href={googleMapsLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-2 inline-flex items-center text-sm font-semibold text-primary hover:text-primary"
                                            >
                                                Get Directions <Navigation className="ml-1 h-4 w-4" />
                                            </a>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
                                        <Clock className="h-6 w-6 text-white" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold leading-7 text-foreground">Opening Hours</h3>
                                        <p className="mt-2 leading-7 text-muted-foreground whitespace-pre-line">
                                            {location.operatingHours || 'Opening hours will be shared when you book or contact us.'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
                                        <Phone className="h-6 w-6 text-white" aria-hidden="true" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold leading-7 text-foreground">Contact</h3>
                                        {hasContactDetails ? (
                                            <p className="mt-2 leading-7 text-muted-foreground">
                                                {phone && (
                                                    <a href={`tel:${phone}`} className="block hover:text-primary">{phone}</a>
                                                )}
                                                {email && (
                                                    <a href={`mailto:${email}`} className="block hover:text-primary">{email}</a>
                                                )}
                                            </p>
                                        ) : (
                                            <p className="mt-2 leading-7 text-muted-foreground">
                                                Contact details will be shared with your booking confirmation.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {pickupInstructions && (
                                    <div className="rounded-md bg-warning-subtle p-4 border border-warning-border">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <Info className="h-5 w-5 text-warning" aria-hidden="true" />
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-warning-foreground">Pickup Instructions</h3>
                                                <div className="mt-2 text-sm text-warning-foreground">
                                                    <p className="whitespace-pre-line">{pickupInstructions}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="relative">
                            {/* Location Image (if available) */}
                            {imageUrl && (
                                <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-muted object-cover shadow-lg mb-8">
                                    <img
                                        src={imageUrl}
                                        alt={location.name}
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                            )}

                            {/* Embedded Google Maps */}
                            {hasAddress && (
                                <div className="aspect-[16/9] overflow-hidden rounded-2xl bg-muted shadow-lg border border-border">
                                    {isTestMode ? (
                                        <div
                                            data-testid="location-map-placeholder"
                                            className="flex h-full w-full items-center justify-center text-sm text-muted-foreground"
                                        >
                                            Interactive map unavailable in test mode.
                                        </div>
                                    ) : (
                                        <iframe
                                            title={`Map of ${location.name}`}
                                            width="100%"
                                            height="100%"
                                            className="border-0"
                                            loading="lazy"
                                            allowFullScreen
                                            referrerPolicy="no-referrer-when-downgrade"
                                            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
                                        ></iframe>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
